import React, {useCallback, useState} from 'react';

import {IconApps} from '@veneer/core';

import {WolfThemeProvider, useWolfTheme} from '../../contexts';
import WFCButton from '../WFCButton/WFCButton';

import {AppSwitcherMenu} from './components/AppSwitcherMenu';
import {Breadcrumbs} from './components/Breadcrumbs';
import {AppSwitcherConfigProvider, useAppSwitcherConfig} from './context/Config';
import {AppSwitcherProps} from './context/types';
import useStyles from './useStyles';

type Props = {
    defaultOpen?: boolean;
    trailing?: React.ReactNode;
};

const Component = ({defaultOpen = false, trailing}: Props) => {
    const {
        i18n,
        currentService,
        breadcrumbs: breadcrumbsProp,
        currentLanguage,
        disableAppSwitcher,
        currentAppHomeUrl,
    } = useAppSwitcherConfig();
    const [open, setOpen] = useState(defaultOpen);

    const handleToggle = useCallback(() => {
        setOpen(curr => !curr);
    }, []);

    const styles = useStyles();

    const breadcrumbs = [
        {
            label: currentService.displayName[currentLanguage].value,
            href: currentAppHomeUrl,
        },
        ...breadcrumbsProp,
    ];

    return (
        <>
            <div className={styles.header}>
                <div className={styles.left}>
                    <div className={styles.toggle}>
                        {!disableAppSwitcher && (
                            <WFCButton
                                aria-label={i18n.openAppSwitcher}
                                onClick={handleToggle}
                                leadingIcon={
                                    <IconApps
                                        size={24}
                                        aria-hidden
                                    />
                                }
                            />
                        )}
                    </div>
                    <Breadcrumbs
                        ariaLabel={i18n.breadcrumbs}
                        items={breadcrumbs}
                    />
                </div>
                <div>{trailing}</div>
            </div>
            <AppSwitcherMenu
                open={open}
                onToggle={handleToggle}
            />
        </>
    );
};

const AppSwitcher = ({config, ...rest}: Props & {config?: Partial<AppSwitcherProps>}) => {
    const theme = useWolfTheme();
    return (
        <WolfThemeProvider
            density={theme.density}
            mode={/* istanbul ignore next */ theme.mode !== 'contrast' ? 'dark' : 'contrast'}
            shape={theme.shape}
        >
            <AppSwitcherConfigProvider config={config}>
                <Component {...rest} />
            </AppSwitcherConfigProvider>
        </WolfThemeProvider>
    );
};

export default AppSwitcher;
